import debounce from 'lodash-es/debounce';
import get from 'lodash-es/get';
import has from 'lodash-es/has';
import pick from 'lodash-es/pick';
import set from 'lodash-es/set';
import sortBy from 'lodash-es/sortBy';
import throttle from 'lodash-es/throttle';
import variables from '../../common/variables';
import utils from '../../common/utils';
import uuid from 'uuid';
import fabric from '../../common/fabric';

import {
  isObjectOfType,
  isActiveSelection,
  getDefaultPropertiesForType,
  objectHasPropCapability,
  ObjectSelection,
} from '../../common/objects';
import * as canvasActions from './canvasActions';
import { select } from './selectionActions';
import { PROP_FIT } from '../../common/properties';

import maxBy from 'lodash-es/maxBy';
import { getCurrentSlideId } from '../reducers/canvasReducer';
import smalltalk from '../../vendor/smalltalk';

export const dispatchModified = (object, doNotDebounce) => async dispatch => {
  if (doNotDebounce) {
    if (!object.parentSymbol && !object.canvas?.editingSymbol) {
      dispatch({ type: 'OBJECT_MODIFIED', object });
    }
  } else {
    debouncedModified(dispatch, object);
  }
};

const debouncedModified = debounce(
  (dispatch, object) => {
    if (!object.parentSymbol && !object.canvas?.editingSymbol) {
      dispatch({ type: 'OBJECT_MODIFIED', object });
    }
  },
  400,
  { leading: true, trailing: true },
);

export const dispatchModifying = object => dispatch => {
  if (!object.parentSymbol) {
    const transform = object
      ? {
          transformMatrix: object?.calcTransformMatrix() || [],
          height: object?.height || null,
          width: object?.width || null,
          action: object?.canvas?._currentTransform?.action || null,
        }
      : null;
    dispatch({ type: 'OBJECT_MODIFYING', object, transform });
  }
};

const debouncedModifying = throttle(
  (dispatch, object, e) => {
    dispatchModifying(object)(dispatch);
  },
  50,
  { leading: false, trailing: true },
);

export const dispatchModifyingDebounced = object => dispatch => {
  debouncedModifying(dispatch, object);
};

const applySelectionStyles = (object, options) => {
  const {
    fontFamily,
    fontStyle,
    fontWeight,
    lineHeight,
    fontSize,
    charSpacing,
    textAlign,
    originY,
    fill,
    underline,
    linethrough,
    styleCase,
  } = options;

  const objectStyles = object.getSelectionStyles(false);
  // Apply on the current selection of text if there is a selection made
  // if selection of all text, apply on object directly (useful to avoid mix
  // of fill on text selection and another fill on the object itself.

  if (object.isEditing) {
    object.setSelectionStyles(options);
  } else {
    // if we directly want to apply fontWeight we remove it first.
    // if we just change fontFamily we'll keep fontWeight if the new font has the variant
    if (fontWeight) {
      object.removeCharStyles('fontWeight');
      object.set({ fontWeight: fontWeight || objectStyles[0].fontWeight });
    }
    if (fontStyle) {
      object.removeCharStyles('fontStyle');
      object.set({ fontStyle });
    }
    // avoid undefined fill (resulting in black or transparent color)
    if (fill) {
      object.removeCharStyles('fill');
      object.set({ fill });
    }
    // vertical text alignement.
    if (originY) {
      const currentCenter = object.getCenterPoint();
      new ObjectSelection(object).setProperty('originY', originY);
      object.setPositionByOrigin(currentCenter, 'center', 'center');
    }
    if (fontSize) {
      object.removeCharStyles('fontSize');
      object.set({
        // to get better fontSize management we reset the scale to apply a normalized fontSize
        fontSize,
        scaleX: 1,
        scaleY: 1,
      });
    }
    if (styleCase) {
      object.removeCharStyles('styleCase');
      object.set({ styleCase });
    }
    if (underline !== undefined) {
      // for this property we need to apply only with styles not on object itself
      // so svg export can be multicolored
      object.setSelectionStyles({ underline }, 0, object.text.length);
    }
    if (linethrough !== undefined) {
      // for this property we need to apply only with styles not on object itself
      // so svg export can be multicolored
      object.setSelectionStyles({ linethrough }, 0, object.text.length);
    }

    if (fontFamily) {
      object.removeCharStyles('fontFamily');
      object.set({ fontFamily });
    }

    object.set({
      lineHeight: lineHeight || object.lineHeight,
      charSpacing: charSpacing || object.charSpacing,
      textAlign: textAlign || object.textAlign,
    });
    object.setCoords();
  }
};

export const setSelectionStyles = (object, options, isModifying) => (
  dispatch,
  getState,
  fabricCanvas,
) => {
  if (isActiveSelection(object)) {
    object.forEachObject(o => {
      applySelectionStyles(o, options);
      o._removeExtraneousStyles && o._removeExtraneousStyles();
    });
  }
  if (isObjectOfType(object, 'title', 'paragraph', 'text')) {
    applySelectionStyles(object, options);
    object._removeExtraneousStyles();
  }
  fabricCanvas.requestRenderAll();
  if (isModifying) {
    dispatch(dispatchModifying(object));
  } else {
    dispatch(dispatchModified(object));
  }
};

export const distribute = (align, object) => (
  dispatch,
  getState,
  fabricCanvas,
) => {
  if (!isActiveSelection(object)) {
    return;
  }

  object.setCoords();
  const distribution = align === 'horizontally' ? 'left' : 'top';
  const size = align === 'horizontally' ? 'width' : 'height';
  const sortedObjects = sortBy(object.getObjects(), [distribution]);
  //
  const groupLeft = object.left;
  const groupTop = object.top;
  //
  let totalSize = 0;
  sortedObjects.forEach(obj => {
    totalSize += get(obj.getBoundingRect(true, true), size);
  });
  // space between each obj
  const space = (get(object, size) - totalSize) / (sortedObjects.length - 1);

  let prevPos = sortedObjects[0].getPointByOrigin('center', 'center');
  let prevBound = get(sortedObjects[0].getBoundingRect(true, false), size) / 2;
  sortedObjects.forEach(obj => {
    const pos = obj.getPointByOrigin('center', 'center');
    const bounds = prevBound + get(obj.getBoundingRect(true, true), size) / 2;
    if (distribution === 'left') {
      pos.x = prevPos.x + bounds + space;
    } else {
      pos.y = prevPos.y + bounds + space;
    }
    obj.setPositionByOrigin(pos, 'center', 'center');
    prevPos = pos;
    prevBound = get(obj.getBoundingRect(true, true), size) / 2;
    obj.setCoords();
  });

  // fabric hack to reset size of bounding box
  object.forEachObject(item => {
    item.setCoords();
    object.removeWithUpdate(item).addWithUpdate(item);
  });
  // fabric hack to reset position of bounding box
  object.set({ left: groupLeft });
  object.set({ top: groupTop });
  object.setCoords();
  saveLastTransform(object, ['left', 'top', 'transformMatrix'])();
  fabricCanvas.requestRenderAll();
  dispatch(dispatchModified(object, true));
};

export const align = (alignment, object) => (
  dispatch,
  getState,
  fabricCanvas,
) => {
  if (object.parentSymbol) {
    return null;
  } else if (isActiveSelection(object)) {
    const group = object;
    group.setCoords();
    const groupWidth = group.getBoundingRect(true, true).width;
    const groupHeight = group.getBoundingRect(true, true).height;
    const scaleFactor = group.scaleX;
    group.forEachObject(obj => {
      const itemWidth = obj.getBoundingRect(true).width * scaleFactor;
      const itemHeight = obj.getBoundingRect(true).height * scaleFactor;
      const absolutePos = obj.getPointByOrigin('center', 'center');
      if (alignment === 'top') {
        absolutePos.y = -groupHeight / 2 + itemHeight / 2;
        obj.setPositionByOrigin(absolutePos, 'center', 'center');
      } else if (alignment === 'bottom') {
        absolutePos.y = groupHeight / 2 - itemHeight / 2;
        obj.setPositionByOrigin(absolutePos, 'center', 'center');
      } else if (alignment === 'left') {
        absolutePos.x = -groupWidth / 2 + itemWidth / 2;
        obj.setPositionByOrigin(absolutePos, 'center', 'center');
      } else if (alignment === 'right') {
        absolutePos.x = groupWidth / 2 - itemWidth / 2;
        obj.setPositionByOrigin(absolutePos, 'center', 'center');
      } else if (alignment === 'verticalCenter') {
        absolutePos.y = 0;
        obj.setPositionByOrigin(absolutePos, 'center', 'center');
      } else if (alignment === 'horizontalCenter') {
        absolutePos.x = 0;
        obj.setPositionByOrigin(absolutePos, 'center', 'center');
      }
    });
    // refresh group for bouding box display ok
    group.forEachObject(item => {
      item.setCoords();
      group.removeWithUpdate(item).addWithUpdate(item);
    });
    saveLastTransform(group, ['left', 'top', 'transformMatrix'])();
    group.setCoords();
  } else if (object) {
    const canvasH = getState().presentationReducer.documentSize.height;
    const canvasW = getState().presentationReducer.documentSize.width;
    const absolutePos = object.getPointByOrigin('center', 'center');
    if (alignment === 'top') {
      absolutePos.y = 0;
      object.setPositionByOrigin(absolutePos, 'center', 'top');
    } else if (alignment === 'bottom') {
      absolutePos.y = canvasH;
      object.setPositionByOrigin(absolutePos, 'center', 'bottom');
    } else if (alignment === 'left') {
      absolutePos.x = 0;
      object.setPositionByOrigin(absolutePos, 'left', 'center');
    } else if (alignment === 'right') {
      absolutePos.x = canvasW;
      object.setPositionByOrigin(absolutePos, 'right', 'center');
    } else if (alignment === 'verticalCenter') {
      absolutePos.y = canvasH / 2;
      object.setPositionByOrigin(absolutePos, 'center', 'center');
    } else if (alignment === 'horizontalCenter') {
      absolutePos.x = canvasW / 2;
      object.setPositionByOrigin(absolutePos, 'center', 'center');
    }
    object.setCoords();
  }
  fabricCanvas.requestRenderAll();
  dispatch(dispatchModified(object, true));
};

export const updateOriginY = (object, originY) => (
  dispatch,
  getState,
  fabricCanvas,
) => {
  if (isActiveSelection(object)) {
    object.forEachObject(obj => {
      const currentCenter = obj.getCenterPoint();
      obj.set('originY', originY);
      obj.setPositionByOrigin(currentCenter, 'center', 'center');
      obj.setCoords();
    });
  } else {
    const currentCenter = object.getCenterPoint();
    object.set('originY', originY);
    object.setPositionByOrigin(currentCenter, 'center', 'center');
  }
  object.setCoords();
  saveLastTransform(object, ['originY'])();
  fabricCanvas.requestRenderAll();
  dispatch(dispatchModified(object, true));
};

const _indexOfObject = (object, objects) => {
  for (let index = 0; index < objects.length; index++) {
    if (object.canvasId === objects[index].canvasId) {
      return index;
    }
  }
  return 0;
};

const _getObjectOrder = object => {
  let objectOrder = 0;
  if (object.type === 'activeSelection') {
    object.forEachObject(o => {
      const index = _indexOfObject(o, object.canvas._objects);
      if (index > objectOrder) {
        objectOrder = index;
      }
    });
  } else {
    objectOrder = _indexOfObject(object, object.canvas._objects);
  }
  return objectOrder;
};

// disregard guidelines when chaning position of object
// direction can be -1 or 1
const findAvailablePosition = (object, direction, startIndex) => {
  let _startIndex = startIndex || object.canvas._objects.indexOf(object);
  if (!startIndex && object.type === 'activeSelection') {
    _startIndex =
      direction === 1
        ? object.canvas._objects.indexOf(
            object._objects[object._objects.length - 1],
          )
        : object.canvas._objects.indexOf(object._objects[0]);
  }
  const objects = object.canvas._objects;
  let newIndex = _startIndex;
  for (
    let index = _startIndex;
    direction === 1 ? index < objects.length : index > 0;
    direction === 1 ? index++ : index--
  ) {
    const element = objects[index + direction];
    if (element && element.type !== 'ludusGuideline') {
      newIndex = index + direction;
      break;
    }
  }
  return newIndex;
};

export const bringToFront = object => (dispatch, getState) => {
  const len = object.canvas._objects.length;
  const nextIndex = findAvailablePosition(object, -1, len);
  object.canvas.moveTo(object, nextIndex, 1);
  dispatch(dispatchModified(object));
};

export const sendToBack = object => (dispatch, getState) => {
  const nextIndex = findAvailablePosition(object, 1, -1);
  object.canvas.moveTo(object, nextIndex);
  dispatch(dispatchModified(object));
};

export const bringForward = object => (dispatch, getState) => {
  const nextIndex = findAvailablePosition(object, 1);
  object.canvas.moveTo(object, nextIndex, 1);
  dispatch(dispatchModified(object));
};

export const sendBackward = object => (dispatch, getState) => {
  const nextIndex = findAvailablePosition(object, -1);
  object.canvas.moveTo(object, nextIndex, -1);
  dispatch(dispatchModified(object));
};

export const fitToCanvas = object => (dispatch, getState, fabricCanvas) => {
  if (!objectHasPropCapability(object, PROP_FIT) || object.parentSymbol) {
    return;
  }
  // resize object to take full canvas space
  const {
    width,
    height,
  } = getState().contentReducer.presentation.canvasDimensions;
  const docRatio = width / height;
  const objectRatio = object.width / object.height;

  if (object.lockUniScaling) {
    if (objectRatio < docRatio) {
      object.scaleToWidth(width, true);
    } else {
      object.scaleToHeight(height, true);
    }
  } else if (isObjectOfType(object, 'circle')) {
    object.set({
      rx: width / 2,
      ry: height / 2,
      scaleX: 1,
      scaleY: 1,
    });
  } else {
    object.set({
      width,
      height,
      scaleX: 1,
      scaleY: 1,
    });
  }
  saveLastTransform(object, [
    'left',
    'top',
    'width',
    'height',
    'transformMatrix',
  ])();
  object.setPositionByOrigin(
    new fabric.Point(width / 2, height / 2),
    'center',
    'center',
  );
  object.setCoords();
  fabricCanvas.requestRenderAll();
  dispatch(dispatchModified(object));
};

export const update = (object, properties, isModifying, doNotDebounce) => (
  dispatch,
  getState,
) => {
  new ObjectSelection(object).setProperties(properties);
  saveLastTransform(object, Object.keys(properties))();
  if (isModifying) {
    dispatch(dispatchModifyingDebounced(object));
  } else {
    dispatch(dispatchModified(object, doNotDebounce));
  }

  if (has(properties, 'ludus.highQuality')) {
    dispatch({ type: 'REFRESH_CANVAS' });
  }
};

export const performAction = (object, action, payload) => () => {
  new ObjectSelection(object).performAction(action, payload);
};

export const saveLastTransform = (object, properties) => () => {
  // store the last edited object to compare while merging props for live collab
  if (!object.parentSymbol) {
    try {
      const objects = [];
      const propertiesAsArary = properties
        ? Array.isArray(properties)
          ? properties
          : Object.keys(properties)
        : [];
      if (object.type === 'activeSelection') {
        object.forEachObject(o => {
          const _o = pick(o.toObjectUnGrouped(), [
            ...propertiesAsArary,
            'canvasId',
          ]);
          objects.push(_o);
        });
      } else {
        const _o = pick(object.toObject(), [...propertiesAsArary, 'canvasId']);
        objects.push(_o);
      }

      sessionStorage.setItem(
        'previousTransform',
        JSON.stringify({
          targets: objects,
          properties: propertiesAsArary,
          date: Date.now(),
        }),
      );
    } catch (error) {
      console.error(error);
    }
  }
};

export const setPositionByOrigin = (object, values, isModifying) => (
  dispatch,
  getState,
  fabricCanvas,
) => {
  const x = 'x' in values ? values.x : object.getPointByOrigin('left', 'top').x;
  const y = 'y' in values ? values.y : object.getPointByOrigin('left', 'top').y;
  const point = new fabric.Point(x, y);
  object.setPositionByOrigin(point, 'left', 'top');
  saveLastTransform(object, ['top', 'left', 'transformMatrix'])();
  object.setCoords();
  fabricCanvas.requestRenderAll();

  if (isModifying) {
    dispatch(dispatchModifying(object));
  } else {
    dispatch(dispatchModified(object));
  }
};

export const setWidth = (object, _width, isModifying) => (
  dispatch,
  getState,
  fabricCanvas,
) => {
  const width = Math.max(1, _width);
  if (object instanceof fabric.Group) {
    if (object._objects.some(o => o.lockUniScaling)) {
      object.lockUniScaling = true;
    }
  }
  if (object.lockUniScaling) {
    const scaledWidth = Number.isNaN(parseInt(width, 10))
      ? object.getScaledWidth()
      : width;

    const scaleX = scaledWidth / object.width;
    const ratio = object.scaleY / object.scaleX;
    object.set({
      scaleX: Math.round(scaleX * 10000) / 10000,
      scaleY: Math.round(ratio * scaleX * 10000) / 10000,
    });
    // } else if (isActiveSelection(object)) {
    //   object.set({ scaleX: width / object.width });
  } else if (object.type === 'ludusPathGroup') {
    const value = Number.isNaN(parseInt(width, 10))
      ? object.getScaledWidth()
      : width;
    const scale = value / object.width;
    object.set({ scaleX: scale });
  } else {
    if (object instanceof fabric.Group) {
      object.forEachObject(o => {
        o.scaleObjectInGroup({
          group: object,
          scaleX: width / object.scaleX / object.width,
          scaleY: 1,
          _by: 'x',
        });
      });
    }
    object.set({
      width: width / object.scaleX,
    });
  }
  saveLastTransform(object, ['width', 'transformMatrix'])();
  object.setCoords();
  fabricCanvas.requestRenderAll();
  if (isModifying) {
    dispatch(dispatchModifying(object));
  } else {
    dispatch(dispatchModified(object));
  }
};

export const setHeight = (object, _height, isModifying) => (
  dispatch,
  getState,
  fabricCanvas,
) => {
  const height = Math.max(1, _height);
  if (
    object.lockUniScaling ||
    isObjectOfType(object, 'text', 'title', 'paragraph')
  ) {
    const value = Number.isNaN(parseInt(height, 10))
      ? object.getScaledWidth()
      : height;
    const scale = value / object.height;
    object.scale(Math.round(scale * 10000) / 10000);
  } else {
    if (object instanceof fabric.Group) {
      object.forEachObject(o => {
        o.scaleObjectInGroup({
          group: object,
          scaleX: 1, // width / object.scaleX / object.width,
          scaleY: height / object.scaleY / object.height,
          _by: 'y',
        });
      });
    }
    object.set({ height: height / object.scaleY });
  }
  saveLastTransform(object, ['height', 'transformMatrix'])();
  object.setCoords();
  fabricCanvas.requestRenderAll();
  if (isModifying) {
    dispatch(dispatchModifying(object));
  } else {
    dispatch(dispatchModified(object));
  }
};

export const setRotationFromCenter = (object, value, isModifying) => (
  dispatch,
  getState,
  fabricCanvas,
) => {
  object.rotate(value);
  saveLastTransform(object, ['angle', 'skewX', 'skewY', 'transformMatrix'])();
  object.setCoords();
  fabricCanvas.requestRenderAll();
  if (isModifying) {
    dispatch(dispatchModifying(object));
  } else {
    dispatch(dispatchModified(object));
  }
};

export const copyStyles = object => dispatch => {
  const selectionStyles = utils.getSelectionStyles(object);
  const copiedStyles = Array.isArray(selectionStyles)
    ? selectionStyles[0]
    : selectionStyles;

  const listStyle = object.getListStyle();
  copiedStyles.listStyle = listStyle || 'none';
  copiedStyles.bulletStyleId = object.bulletStyleId || 0;
  copiedStyles.charSpacing = object.charSpacing || 0;
  copiedStyles.fontSize *= object.scaleX || 1;
  copiedStyles.textAlign = object.textAlign;
  copiedStyles.lineHeight = object.lineHeight || 1;
  if (!object.isEditing) {
    copiedStyles.originY = object.originY;
  }
  dispatch({ type: 'STYLES_COPIED', styles: copiedStyles });
  return copiedStyles;
};

export const pasteStyles = (object, copiedStyles) => dispatch => {
  object.charSpacing = copiedStyles.charSpacing;
  object.lineHeight = copiedStyles.lineHeight;
  object.fontSize = copiedStyles.fontSize;
  object.textAlign = copiedStyles.textAlign;
  copiedStyles.listStyle && object.setListStyle(copiedStyles.listStyle, true);
  copiedStyles.bulletStyleId &&
    object.setBulletStyle(copiedStyles.bulletStyleId, true);
  dispatch(setSelectionStyles(object, copiedStyles));
};

export const remove = object => (dispatch, getState, fabricCanvas) => {
  if (isActiveSelection(object)) {
    fabricCanvas._discardActiveObject();
    object.forEachObject(o => {
      fabricCanvas.remove(o);
    });
    dispatch({
      type: 'OBJECT_REMOVED',
      objects: object.getObjects().map(o => o.canvasId),
    });
  } else {
    fabricCanvas.remove(object);
    fabricCanvas.requestRenderAll();
    dispatch({
      type: 'OBJECT_REMOVED',
      objects: [object].map(o => o.canvasId),
    });
  }
};

export const group = object => (dispatch, getState, fabricCanvas) => {
  if (!isActiveSelection(object) || object.canvas.editingSymbol) {
    return;
  }

  if (object.scaleX !== object.scaleY) {
    // when grouping active selection with unfixed ratio
    // we must "apply" the transform to each child, to make sure
    // the group is actually with fix ratio next
    object.forEachObject(o => {
      const matrix = o.calcTransformMatrix();
      const options = fabric.util.qrDecompose(matrix);
      o.set({
        flipX: false,
        flipY: false,
        scaleX: options.scaleX,
        scaleY: options.scaleY,
        skewX: options.skewX,
        skewY: options.skewY,
        angle: options.angle,
        left: o.left * object.scaleX,
        top: o.top * object.scaleY,
      });
    });
    object.set({
      width: object.width * object.scaleX,
      height: object.height * object.scaleY,
      scaleX: 1,
      scaleY: 1,
    });
  }

  const maxObjectOrder = _getObjectOrder(object);
  const order = maxObjectOrder - object._objects.length + 1;

  const g = object.toGroup();
  g.set(getDefaultPropertiesForType('group'));
  set(g, 'canvasId', uuid.v4());
  g.moveTo(order);
  // set(g, 'ludus.order', order);
  dispatch(select(g));
  fabricCanvas.requestRenderAll();
  dispatch(dispatchModified(g));
};

export const ungroup = groupObject => (dispatch, getState, fabricCanvas) => {
  if (groupObject.ludus.locked) {
    set(groupObject, 'ludus.locked', false);
  }
  groupObject.forEachObject(o => {
    if (o.applyGroupScale) {
      o.applyGroupScale();
    }
    o.set({
      opacity: o.opacity * groupObject.opacity,
    });
  });

  let zOrder = _getObjectOrder(groupObject);
  const selectionGroup = groupObject.toActiveSelection();
  selectionGroup.opacity = 1;
  canvasActions.resetActiveSelection();

  const batchUpdate = {};
  selectionGroup.forEachObject(o => {
    const json = o.toJSON(variables.propsToSave);
    // We have to translate the object now relative to the group
    // center, back to the position relative to the slide.
    json.top += selectionGroup.top + selectionGroup.width / 2;
    json.left += selectionGroup.left + selectionGroup.width / 2;
    json.ludus.order = zOrder;
    o.moveTo(zOrder);
    o.dirty = true;

    batchUpdate[o.canvasId] = json;

    zOrder++;
  });
  // firebaseBatchUpdate(batchUpdate, getState);

  dispatch(select(selectionGroup));
  fabricCanvas.requestRenderAll();
  dispatch({ type: 'OBJECT_UNGROUPED', object: selectionGroup });
  // selectionGroup.forEachObject(o => dispatch({ type: 'OBJECT_ADDED', o }));
};

export const restoreTextEditingFocus = object => () => {
  // when editing a text, if you interact with properties panel
  // it can lose the focus (but still think it has).
  // This function helps restore the focus in that situation.
  if (object.setFocusBack && object.isEditing) {
    object.setFocusBack();
  }
};

export const makeEditable = object => (dispatch, getState, fabricCanvas) => {
  if (!isObjectOfType(object, 'group', 'path-group', 'image')) {
    return;
  }

  if (isObjectOfType(object, 'image')) {
    if (object.ludus.size > 30000) {
      console.info('SVG image too big to be ungrouped');
      smalltalk.alert(
        'Oops',
        'Sorry, this SVG image is too big or too complex to be ungrouped.',
      );
      return;
    }
    const url = object.ludus.originalUrl || object.ludus.url;
    const objectMatrix = object.calcTransformMatrix();
    fabric.loadSVGFromURL(url, (_objects, options) => {
      const { bbox } = options; // bbox is given by official svg.getBBox() x, y, w, h. X and Y is top left of the box.
      // we adapt the x and y to fabric group coords (0, 0 is in the middle of the group)
      const left = bbox.x + (bbox.width - object._image.width) * 0.5;
      const top = bbox.y + (bbox.height - object._image.height) * 0.5;

      var group = new fabric.Group(_objects, {
        left: object.left,
        top: object.top,
        type: 'group',
      });
      group.canvas = object.canvas;

      if (object.width * object.scaleX > object.height * object.scaleY) {
        group.scaleToWidth(bbox.width * object.scaleX * object._image.scaleX);
      } else {
        group.scaleToHeight(bbox.height * object.scaleY * object._image.scaleY);
      }
      const pos = fabric.util.transformPoint({ x: left, y: top }, objectMatrix);
      group.angle = object.angle;
      group.setPositionByOrigin(pos, 'center', 'center');
      group.forEachObject(o => {
        if (!o.type.includes('ludus')) {
          set(o, 'ludus.type', 'path');
        }
      });

      object.canvas.add(group);
      object.canvas.remove(object);
      const selection = group.toActiveSelection();
      dispatch(select(selection));
      dispatch(dispatchModified(selection));
    });
  } else {
    object.forEachObject(o => {
      if (o.applyGroupScale) {
        o.applyGroupScale();
      }
      if (!o.type.includes('ludus')) {
        set(o, 'ludus.type', 'path');
      }

      o.set({
        objectCaching: false,
        noScaleCache: false,
      });
    });

    const selectionGroup = object.toActiveSelection();
    selectionGroup.opacity = 1;
    dispatch(select(selectionGroup));
    dispatch(dispatchModified(selectionGroup));

    // Deselect and select again to refresh its bounds
    fabricCanvas.discardActiveObject();
    const selection = new fabric.ActiveSelection(selectionGroup.getObjects(), {
      canvas: fabricCanvas,
    });

    fabricCanvas.setActiveObject(selection);
  }
  fabricCanvas.requestRenderAll();
};

/**
 * Prepare the provided object for conversion as a symbol (smart object)
 * We need to convert the object to a fabric Group
 *
 * @param object
 */

export const prepareForSymbol = object => (dispatch, getState, canvas) =>
  new Promise(resolve => {
    let symbolGroup;

    if (isActiveSelection(object)) {
      const symbolObjects = object.getObjects();
      canvas.discardActiveObject();
      object.forEachObject(o => {
        canvas.remove(o);
      });
      symbolGroup = new fabric.Group(symbolObjects);
      canvas.add(symbolGroup);
    } else if (isObjectOfType(object, 'group')) {
      const symbolObjects = object.getObjects();
      object.ungroupOnCanvas();
      object.forEachObject(o => {
        canvas.remove(o);
      });
      symbolGroup = new fabric.Group(symbolObjects);
      canvas.remove(object).add(symbolGroup);
    } else {
      symbolGroup = new fabric.Group([object]);
      canvas.remove(object).add(symbolGroup);
    }
    symbolGroup.background = canvas.backgroundColor;
    resolve(symbolGroup);
  });

/**
 * When a user creates a symbol, the active selection is converted into a group and then
 * saved in our database. We will then replace the group on the canvas by a symbol instance,
 * that will contain a reference to the symbol id
 *
 * @param g
 * @param symbolData
 */

export const replaceGroupBySymbol = (g, symbolData) => (
  dispatch,
  getState,
  fabricCanvas,
) =>
  new Promise((resolve, reject) => {
    fabric.LudusSymbol.register(
      symbolData.id,
      symbolData.name,
      JSON.parse(symbolData.content),
    );
    const overrides = {
      reference: symbolData.id,
      ...pick(g, ['left', 'top', 'width', 'height']),
    };
    dispatch(
      canvasActions.addObject('symbol', overrides, () => {
        fabricCanvas.remove(g);
      }),
    )
      .then(() => {
        resolve();
      })
      .catch(error => {
        reject(error);
      });
  });

export const lock = object => (dispatch, getState, fabricCanvas) => {
  if (object.canvas.editingSymbol) {
    return false;
  } else if (isActiveSelection(object)) {
    object.forEachObject(o => {
      o.set(variables.lockProperties);
      o.ludus.locked = true;
    });
    fabricCanvas.discardActiveObject();
    fabricCanvas.requestRenderAll();
  } else {
    object.ludus.locked = true;
    object.set(variables.lockProperties);
    fabricCanvas.discardActiveObject();
    fabricCanvas.requestRenderAll();
  }
  dispatch({ type: 'OBJECT_LOCKED', object });
};

export const unlock = object => (dispatch, getState, fabricCanvas) => {
  object.set(variables.regularProperties);
  if (object.ludus) {
    object.ludus.locked = false;
  }
  fabricCanvas.setActiveObject(object);
  fabricCanvas.requestRenderAll();
  const otherObjectLocked = fabricCanvas.getObjects().some(o => o.ludus.locked);
  if (otherObjectLocked) {
    dispatch({ type: 'OBJECT_UNLOCKED', object });
  } else {
    dispatch({ type: 'ALL_UNLOCKED', object });
  }
};

export const move = (object, property, diff) => (
  dispatch,
  getState,
  fabricCanvas,
) => {
  if (object.canvas.editingSymbol) {
    return;
  }
  const properties = {};
  const value = object.get(property);
  properties[property] = value + diff;
  saveLastTransform(object, ['left', 'top', 'transformMatrix'])();
  object.set(properties).setCoords();
  fabricCanvas.requestRenderAll();

  dispatchModifying(object)(dispatch);
  dispatch(dispatchModified(object));
};

export const dispatchLockSilent = object => dispatch => {
  dispatch({ type: 'OBJECT_LOCKED_SILENT', object });
};

export const dispatchAdded = object => (dispatch, getState) => {
  !object.isEditing && window.canvasContainer && window.canvasContainer.focus();
  const addIdAndOrder = object => {
    if (!('canvasId' in object)) {
      set(object, 'canvasId', uuid.v4());
    }

    if (!('order' in object.ludus)) {
      const presentation = getState().contentReducer.presentation;
      const slideObjects =
        presentation.slides[getCurrentSlideId(presentation)].content.objects;
      const highestObject = slideObjects
        ? maxBy(Object.values(slideObjects), o => o.ludus?.order || 0)
        : null;

      object.ludus.order = highestObject ? highestObject.ludus.order + 1 : 0;
    }
  };
  if (Array.isArray(object)) {
    object.forEach(o => {
      addIdAndOrder(o);
    });
  } else {
    addIdAndOrder(object);
  }
  if (
    object.type === 'ludusImage' &&
    (!object.ludus?.originalWidth || !object.width)
  ) {
    // Don't dispatch if image is not ready/fully uploaded
    return false;
  }

  dispatch({ type: 'OBJECT_ADDED', object });
};

export const dispatchTextChanged = object => dispatch => {
  dispatch({ type: 'TEXT_CHANGED', object });
};

export const dispatchImageCroppingExited = object => dispatch => {
  dispatch({ type: 'IMAGE_CROPPING_EXITED', object });
};

export const dispatchImageCroppingEntered = object => dispatch => {
  dispatch({ type: 'IMAGE_CROPPING_ENTERED', object });
};

export const dispatchTextEditingEntered = object => dispatch => {
  dispatch({ type: 'TEXT_EDITING_ENTERED', object });
};

export const dispatchTextEditingExited = object => (dispatch, getState) => {
  dispatch({ type: 'TEXT_EDITING_EXITED', object });
  dispatch(dispatchModified(object, true));
};

export const dispatchTextSelectionChanged = object => dispatch => {
  dispatch({ type: 'TEXT_SELECTION_CHANGED', object });
};
export const dispatchSymbolEditingExited = object => dispatch => {
  dispatch({ type: 'SYMBOL_EDITING_EXITED', object });
};

export const dispatchSymbolEditingEntered = object => dispatch => {
  dispatch({ type: 'SYMBOL_EDITING_ENTERED', object });
};

export const dispatchSymbolModified = object => dispatch => {
  dispatch({ type: 'SYMBOL_MODIFIED', object });
};
